import React, { useState, useEffect } from 'react';
import classnames from 'classnames';

import { SidesContainer } from '../components/Container';
import ImageCaption from './ImageCaption';
import { useSwipeable } from 'react-swipeable';
import ArrowLeft from '../assets/arrow_left_stroked.svg';
import ArrowRight from '../assets/arrow_right_stroked.svg';

import * as css from './Images.module.css';
import * as grid from '../styles/grid.module.css';

const ImagesContainer = ({ children }) => {
  // Filter out captions and images
  // We do this because StaticImage cannot be used inside another component,
  // so the captions and images sit next to each other in children.
  const images = [];
  const captions = [];
  const els = React.Children.toArray(children);
  for (let i = 0; i < els.length; i++) {
    if (els[i].type === ImageCaption) {
      captions.push(els[i]);
    } else {
      images.push(els[i]);
      if (!els[i + 1] || els[i + 1].type !== ImageCaption) {
        captions.push(null);
      }
    }
  }

  return (
    <SidesContainer bottomLine className={css.root}>
      <Images images={images} captions={captions} />
    </SidesContainer>
  );
};

const Images = ({
  images,
  captions,
  autoPlay = false,
  advanceDelay = 3000
}) => {
  const [current, setCurrent] = useState(0);
  const isCarousel = images.length > 1;
  const slidePercentage = images.length > 0 ? 100 / images.length : 100;
  const railWidth = images.length * 100;
  const firstImageData = images[0].props.__imageData;
  useEffect(() => {
    if (autoPlay) {
      const interval = setInterval(
        () => setCurrent(cur => (cur + 1) % images.length),
        advanceDelay
      );
      return () => clearInterval(interval);
    }
  }, [advanceDelay, autoPlay, images.length]);

  const nextImage = () => {
    setCurrent(cur => (cur + 1) % images.length);
  };

  const prevImage = () => {
    setCurrent(cur => (cur + images.length - 1) % images.length);
  };

  const handlers = useSwipeable({
    onSwipedLeft: nextImage,
    onSwipedRight: prevImage,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  const dots = images.map((child, i) => (
    <button
      aria-label="carousel button"
      className={classnames({ [css.selected]: current === i })}
      onClick={() => setCurrent(i)}
      tabIndex={i + 1}
      key={`dot-button-${i}`}
    />
  ));

  return (
    <div
      className={classnames(grid.col, css.images, {
        [css.singleImage]: dots.length <= 1
      })}
    >
      <div className={css.carouselContainer}>
        <div
          className={classnames(css.arrowContainer, {
            [css.clickable]: isCarousel
          })}
          role={'button'}
          tabIndex="0"
          onClick={prevImage}
          onKeyDown={e => e.key === 'Enter' && prevImage()}
        >
          {isCarousel && <ArrowLeft className={css.arrowLeft} />}
        </div>
        <div className={css.railContainer}>
          <div
            className={css.rail}
            style={{
              width: `${railWidth}%`,
              transform: `translateX(-${Math.min(
                slidePercentage * current,
                100 - slidePercentage
              )}%)`
            }}
            {...handlers}
          >
            {images.map((img, i) => (
              <div
                key={`slide-${i}`}
                className={classnames(css.slide, {
                  [css.spacing]: !captions[i]
                })}
                style={{
                  flex: `0 0 ${slidePercentage}%`
                }}
                onClick={nextImage}
                role="button"
              >
                {/* this hack allows fixing the caroussel height to the first image's proportions */}
                <div
                  className={css.image}
                  style={{
                    paddingBottom: `${
                      firstImageData
                        ? (firstImageData.height / firstImageData.width) * 100
                        : 66
                    }%`,
                    height: 0
                  }}
                >
                  {img}
                </div>
                {captions && captions[i]}
              </div>
            ))}
          </div>
        </div>
        <div
          className={classnames(css.arrowContainer, {
            [css.clickable]: isCarousel
          })}
          role={'button'}
          tabIndex="0"
          onClick={nextImage}
          onKeyDown={e => e.key === 'Enter' && nextImage()}
        >
          {isCarousel && <ArrowRight className={css.arrowRight} />}
        </div>
      </div>
      {dots.length > 1 && <div className={css.dots}>{dots}</div>}
    </div>
  );
};

export default ImagesContainer;
